// Lib
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import graphQLClientFactory from 'lib/graphql-client-factory';

export type AccordionListProps = ComponentProps & { staticProps?: Result };

export type Ingredient = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
  };
};

export type Role = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
    definition: {
      value: string;
    };
  };
};
export type Language = {
  name: string;
};
export type ActiveCountry = {
  name: string;
  item: {
    languageList: {
      targetItems: Language[];
    };
  };
  id: string;
};
export type FormulaIngredient = {
  name: string;
  ingredient: Ingredient;
  role: Role;
  allergen: {
    value: string;
  };
  activeIngredientCountryMapping: {
    targetItems: ActiveCountry[];
  };
};
export type Fragrance = {
  name: string;
  oil: {
    value: string;
  };
  fragrances: {
    targetItems: {
      name: string;
      title: {
        value: string;
      };
      allergen: {
        value: string;
      };
    }[];
  };
};
export type Result = {
  activeIngredients: FormulaIngredient[];
  otherIngredients: FormulaIngredient[];
  isRegistered: boolean;
  fragrances: Fragrance[];
};

export type FormulaIngredients = {
  item: {
    children: {
      total: string;
      results: FormulaIngredient[];
    };
  };
};

export type FormulaFragrances = {
  item: {
    children: {
      total: string;
      results: Fragrance[];
    };
  };
};

const IngredientAccordion = (props: AccordionListProps): JSX.Element => {
  const { activeIngredients, otherIngredients, isRegistered, fragrances } =
    props?.staticProps || {};

  return (
    <div>
      <h1>Ingredient Information</h1>
      <section>
        <h2>Active Ingredients</h2>
        {activeIngredients && activeIngredients?.length > 0 ? (
          <ul>
            {activeIngredients.map((item: FormulaIngredient, index: number) => (
              <>
                <li key={index}>
                  <p>Ingredient Name: {item?.ingredient?.targetItem?.title?.value}</p>
                  <p>Role: {item?.role?.targetItem?.title?.value}</p>
                  <p>Definition: {item?.role?.targetItem?.definition?.value}</p>
                  <p>isAllergen: {item?.allergen?.value}</p>
                </li>
              </>
            ))}
          </ul>
        ) : (
          <p>No active ingredients found.</p>
        )}
      </section>
      <br />
      <br />
      {/* Other Ingredients */}
      <section>
        <h2>Other Ingredients</h2>
        {otherIngredients && otherIngredients.length > 0 ? (
          <ul>
            {otherIngredients.map((item, index) => (
              <>
                <li key={index}>
                  <p>Ingredient Name: {item?.ingredient?.targetItem?.title?.value}</p>
                  <p>Role: {item?.role?.targetItem?.title?.value}</p>
                  <p>Definition: {item?.role?.targetItem?.definition?.value}</p>
                  <p>isAllergen: {item?.allergen?.value}</p>
                </li>
              </>
            ))}
          </ul>
        ) : (
          <p>No other ingredients found.</p>
        )}
      </section>
      <br />
      <br />
      <section>
        <h2>Other Ingredients</h2>
        {fragrances && fragrances.length > 0 ? (
          <>
            {fragrances.map((fragranceItem, fIndex) => (
              <div key={fIndex}>
                {/* If you want to render fragrance targetItems */}
                <p>OIL: {fragranceItem?.oil?.value}</p>
                <br />
                {fragranceItem?.fragrances?.targetItems?.map((target, tIndex) => (
                  <ul key={tIndex}>
                    <li>
                      <p>Fragrance Name: {target?.title?.value}</p>
                      <p>isAllergen: {target?.allergen?.value}</p>
                    </li>
                  </ul>
                ))}
              </div>
            ))}
          </>
        ) : (
          <p>No other ingredients found.</p>
        )}
      </section>
      <br />
      <br />
      {/* Registration Status */}
      <section>
        <h2>Registration Status</h2>
        <p>{isRegistered ? 'Registered' : 'Not Registered'}</p>
      </section>
    </div>
  );
};
export default IngredientAccordion;

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const graphQLClient = graphQLClientFactory({});
  const site = layoutData?.sitecore?.context?.site?.name;
  if (site?.toLocaleLowerCase() !== 'wiscj') {
    return {
      staticProps: null,
    };
  }
  const formula =
    layoutData?.sitecore?.route?.fields?.productInformation &&
    'fields' in layoutData?.sitecore?.route?.fields?.productInformation
      ? layoutData?.sitecore?.route?.fields?.productInformation?.fields?.formula
      : null;
  const formulaId = formula && 'id' in formula ? formula?.id : '';
  const language = layoutData?.sitecore?.context?.language;

  const data = await graphQLClient.request<FormulaIngredients>(query, {
    path: formulaId,
    language: language,
  });

  const formulaIngredients = data && data?.item?.children?.results;

  let isActive = false;
  const result: Result = {
    activeIngredients: [],
    otherIngredients: [],
    isRegistered: false,
    fragrances: [],
  };

  if (formulaIngredients && formulaIngredients.length > 0) {
    for (const formulaIngredient of formulaIngredients) {
      const activeCountries = formulaIngredient?.activeIngredientCountryMapping?.targetItems;
      if (activeCountries && activeCountries.length > 0) {
        for (const country of activeCountries) {
          const countryData = await graphQLClient.request<ActiveCountry>(countryQuery, {
            path: country?.id,
            language: 'en',
          });
          const languageItems = countryData?.item?.languageList?.targetItems;
          for (const lang of languageItems) {
            if (language?.toLocaleLowerCase() === lang?.name?.toString().toLocaleLowerCase()) {
              isActive = true;
              break;
            }
          }
          if (isActive) {
            result.isRegistered = true;
            result.activeIngredients.push(formulaIngredient);
          } else {
            result.otherIngredients.push(formulaIngredient);
          }
        }
      } else {
        result.otherIngredients.push(formulaIngredient);
      }
    }
  }

  const fragranceData = await graphQLClient.request<FormulaFragrances>(fragranceQuery, {
    path: formulaId,
    language: language,
  });

  const formulaFragrances = fragranceData?.item?.children?.results ?? [];
  result.fragrances.push(...formulaFragrances);

  console.log('fragranceData:', fragranceData);
  return {
    staticProps: result,
  };
};

const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{89BC1BBC-B4D7-4580-97DF-2B20E23E0124}"]) {
        total
        results {
          ... on FormulaIngredient {
            name
            allergen: field(name: "allergen") {
              value
            }
            ingredient: field(name: "ingredient") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                }
              }
            }
            role: field(name: "role") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            activeIngredientCountryMapping: field(name: "activeIngredientCountryMapping") {
              ... on MultilistField {
                targetItems {
                  ... on CountryName {
                    name
                    id
                    title: field(name: "title") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const fragranceQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{9B396867-3CBF-4575-9917-0D674E3E8F33}"]) {
        total
        results {
          ... on FragranceList {
            name
            oil: field(name: "oil") {
              value
            }
            fragrances: field(name: "fragrances") {
              ... on MultilistField {
                targetItems {
                  ... on Fragrance {
                    name
                    title: field(name: "title") {
                      value
                    }
                    allergen: field(name: "allergen") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const countryQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      languageList: field(name: "languageList") {
        ... on MultilistField {
          targetItems {
            id
            name
          }
        }
      }
    }
  }
`;
